import { useState } from "react";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import AcceptTerms from "./AcceptTerms";

const initialForm = { name: "", email: "", organization: "", phone: "", timeline: "Not sure yet", message: "" };

export function QuoteModal({ service, onClose }) {
  const [form, setForm] = useState(initialForm);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const message = `Service requested: ${service.title} (Estimated range: ${service.priceRange})\nTimeline: ${form.timeline}\nPhone: ${form.phone || "—"}\n\n${form.message || "No additional details provided."}`;
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        organization: form.organization,
        subject: `Quote Request: ${service.title}`,
        message,
      });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not send request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="quote-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid="quote-modal-overlay" />
      <div className="relative w-full max-w-lg card-dark bg-[var(--surface)] p-8 max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-[var(--text-2)] hover:text-white transition-colors"
          data-testid="quote-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        {!done ? (
          <>
            <div className="overline text-[var(--red)] mb-2">Request a Quote</div>
            <h2 className="font-display text-2xl font-semibold tracking-tight mb-1">{service.title}</h2>
            <p className="text-sm text-[var(--silver)] mb-6">Estimated range: {service.priceRange}</p>

            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="overline mb-2 block">Name</label>
                  <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="input-dark" data-testid="quote-name" />
                </div>
                <div>
                  <label className="overline mb-2 block">Email</label>
                  <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-dark" data-testid="quote-email" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="overline mb-2 block">Organization</label>
                  <input value={form.organization} onChange={(e) => set("organization", e.target.value)} className="input-dark" data-testid="quote-org" />
                </div>
                <div>
                  <label className="overline mb-2 block">Phone</label>
                  <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input-dark" data-testid="quote-phone" />
                </div>
              </div>
              <div>
                <label className="overline mb-2 block">Timeline</label>
                <select value={form.timeline} onChange={(e) => set("timeline", e.target.value)} className="input-dark" data-testid="quote-timeline">
                  <option>ASAP</option>
                  <option>1–3 months</option>
                  <option>3–6 months</option>
                  <option>Not sure yet</option>
                </select>
              </div>
              <div>
                <label className="overline mb-2 block">Project details (optional)</label>
                <textarea
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  className="input-dark"
                  rows={4}
                  placeholder="Funder, deadline, population, scope…"
                  data-testid="quote-message"
                />
              </div>
              <AcceptTerms checked={acceptedTerms} onChange={setAcceptedTerms} testId="quote-accept-terms" />
              {error && <div className="text-sm text-[var(--red)]" data-testid="quote-error">{error}</div>}
              <button type="submit" disabled={submitting} className="btn-red w-full justify-center" data-testid="quote-submit">
                {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : "Send Quote Request"}
              </button>
            </form>
          </>
        ) : (
          <div className="py-8 text-center" data-testid="quote-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h2 className="font-display text-2xl font-semibold tracking-tight mb-2">Request sent</h2>
            <p className="text-[var(--text-2)] mb-6">
              Thanks — we'll review your {service.title} request and follow up within 1 business day.
            </p>
            <button onClick={onClose} className="btn-ghost" data-testid="quote-close-success">Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
